import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AnimatePresence } from 'framer-motion'

// Error Handling
import ErrorBoundary from './components/ErrorBoundary'

// Providers
import { AuthProvider } from './contexts/AuthContext'
import { WebSocketProvider } from './components/providers/WebSocketProvider'

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute'
import Login from './pages/Login'

// Layout (always loaded)
import Layout from './components/layout/Layout'

// Dashboard loads eagerly (landing page)
import Dashboard from './pages/Dashboard'

// Lazy-loaded pages -- each becomes its own chunk
const Vulnerabilities = lazy(() => import('./pages/Vulnerabilities'))
const Patches = lazy(() => import('./pages/Patches'))
const Deployments = lazy(() => import('./pages/Deployments'))
const RollbackHistory = lazy(() => import('./pages/RollbackHistory'))
const Monitoring = lazy(() => import('./pages/Monitoring'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Settings = lazy(() => import('./pages/Settings'))
const ScanUpload = lazy(() => import('./pages/ScanUpload'))
const DropZoneDemo = lazy(() => import('./pages/DropZoneDemo'))
const ApprovalQueue = lazy(() => import('./pages/ApprovalQueue'))
const Tickets = lazy(() => import('./pages/Tickets'))
const Playbooks = lazy(() => import('./pages/Playbooks'))
const Policies = lazy(() => import('./pages/Policies'))
const Connectors = lazy(() => import('./pages/Connectors'))
const Exceptions = lazy(() => import('./pages/Exceptions'))
const MSSP = lazy(() => import('./pages/MSSP'))
const Compliance = lazy(() => import('./pages/Compliance'))
const KevAndCompliance = lazy(() => import('./pages/KevAndCompliance'))
const AuditLog = lazy(() => import('./pages/AuditLog'))
const Assets = lazy(() => import('./pages/Assets'))
const Agents = lazy(() => import('./pages/Agents'))
const Scanner = lazy(() => import('./pages/Scanner'))
const Activity = lazy(() => import('./pages/Activity'))
const PipelineLive = lazy(() => import('./pages/PipelineLive'))
const CloudPosture = lazy(() => import('./pages/CloudPosture'))
const AttackPaths = lazy(() => import('./pages/AttackPaths'))
const CompensatingControls = lazy(() => import('./pages/CompensatingControls'))
const SafetyDashboard = lazy(() => import('./pages/SafetyDashboard'))
const NotFound = lazy(() => import('./pages/NotFound'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="h-8 w-8 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin" />
    </div>
  )
}

function PageWithErrorBoundary({ children }) {
  return <ErrorBoundary>{children}</ErrorBoundary>
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <WebSocketProvider>
            <div className="min-h-screen bg-cyber-darker">
              {/* Animated background gradient */}
              <div className="fixed inset-0 bg-gradient-to-br from-cyber-dark via-cyber-darker to-cyber-dark opacity-50 pointer-events-none" />

              {/* Grid overlay */}
              <div className="fixed inset-0 cyber-grid opacity-30 pointer-events-none" />

              {/* Main content */}
              <div className="relative z-10">
                <Suspense fallback={<PageLoader />}>
                  <AnimatePresence mode="wait">
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<Login />} />
                      {/* Demo drop-zone is a dev/sales sandbox — never expose it unauthenticated in production. */}
                      {import.meta.env.DEV && <Route path="/demo" element={<DropZoneDemo />} />}

                      {/* Protected routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<PageWithErrorBoundary><Dashboard /></PageWithErrorBoundary>} />
                        <Route path="vulnerabilities" element={<PageWithErrorBoundary><Vulnerabilities /></PageWithErrorBoundary>} />
                        <Route path="patches" element={<PageWithErrorBoundary><Patches /></PageWithErrorBoundary>} />
                        <Route path="deployments" element={<PageWithErrorBoundary><Deployments /></PageWithErrorBoundary>} />
                        <Route path="deployments/rollbacks" element={<PageWithErrorBoundary><RollbackHistory /></PageWithErrorBoundary>} />
                        <Route path="monitoring" element={<PageWithErrorBoundary><Monitoring /></PageWithErrorBoundary>} />
                        <Route path="analytics" element={<PageWithErrorBoundary><Analytics /></PageWithErrorBoundary>} />
                        <Route path="approvals" element={<PageWithErrorBoundary><ApprovalQueue /></PageWithErrorBoundary>} />
                        <Route path="pipeline/live" element={<PageWithErrorBoundary><PipelineLive /></PageWithErrorBoundary>} />
                        <Route path="tickets" element={<PageWithErrorBoundary><Tickets /></PageWithErrorBoundary>} />
                        <Route path="playbooks" element={<PageWithErrorBoundary><Playbooks /></PageWithErrorBoundary>} />
                        <Route path="policies" element={<PageWithErrorBoundary><Policies /></PageWithErrorBoundary>} />
                        <Route path="clients" element={<PageWithErrorBoundary><MSSP /></PageWithErrorBoundary>} />
                        <Route path="connectors" element={<PageWithErrorBoundary><Connectors /></PageWithErrorBoundary>} />
                        <Route path="exceptions" element={<PageWithErrorBoundary><Exceptions /></PageWithErrorBoundary>} />
                        <Route path="compliance" element={<PageWithErrorBoundary><Compliance /></PageWithErrorBoundary>} />
                        <Route path="kev" element={<PageWithErrorBoundary><KevAndCompliance /></PageWithErrorBoundary>} />
                        <Route path="cloud-posture" element={<PageWithErrorBoundary><CloudPosture /></PageWithErrorBoundary>} />
                        <Route path="attack-paths" element={<PageWithErrorBoundary><AttackPaths /></PageWithErrorBoundary>} />
                        <Route path="compensating-controls" element={<PageWithErrorBoundary><CompensatingControls /></PageWithErrorBoundary>} />
                        <Route path="audit" element={<PageWithErrorBoundary><AuditLog /></PageWithErrorBoundary>} />
                        <Route path="assets" element={<PageWithErrorBoundary><Assets /></PageWithErrorBoundary>} />
                        <Route path="agents" element={<PageWithErrorBoundary><Agents /></PageWithErrorBoundary>} />
                        <Route path="activity" element={<PageWithErrorBoundary><Activity /></PageWithErrorBoundary>} />
                        <Route path="network-scanner" element={<PageWithErrorBoundary><Scanner /></PageWithErrorBoundary>} />
                        <Route path="safety" element={<PageWithErrorBoundary><SafetyDashboard /></PageWithErrorBoundary>} />
                        <Route path="scan" element={<PageWithErrorBoundary><ScanUpload /></PageWithErrorBoundary>} />
                        <Route path="settings" element={<PageWithErrorBoundary><Settings /></PageWithErrorBoundary>} />
                      </Route>

                      {/* 404 catch-all */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </AnimatePresence>
                </Suspense>
              </div>

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  className: 'glass-card',
                  style: {
                    background: 'rgba(15, 23, 42, 0.9)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(0, 217, 255, 0.2)',
                    color: '#f1f5f9',
                  },
                  success: {
                    iconTheme: {
                      primary: '#00ff88',
                      secondary: '#0a0e27',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#ff0055',
                      secondary: '#0a0e27',
                    },
                  },
                }}
              />
            </div>
          </WebSocketProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
